import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { LEGAL } from '../../Config/legal';
import {
  PLATFORM_SHARE_RATE,
  DEFAULT_DEPOSIT_RATE,
  MARKETPLACE_TERMS_VERSION,
  percent,
} from '../../Config/marketplace';

const legalPages = {
  '/terms': {
    title: "Conditions générales d’utilisation et d’intermédiation",
    intro: `Version ${MARKETPLACE_TERMS_VERSION}. Ce document est un modèle opérationnel à faire valider par un avocat français avant la mise en production commerciale.`,
    sections: [
      ['1. Objet et rôle de la plateforme', [
        `${LEGAL.platformName} fournit un service numérique de mise en relation, de communication, de gestion documentaire, de réputation et de paiement entre des clients et des artisans juridiquement indépendants.`,
        `Le contrat de travaux est conclu directement entre le client et l’artisan. Sauf mention explicite contraire, ${LEGAL.platformName} n’est ni l’entrepreneur, ni le maître d’œuvre, ni l’employeur de l’artisan, ni le vendeur de la prestation de travaux.`,
        `L’artisan reste seul responsable de ses prix, de son devis, de l’exécution, de ses obligations fiscales et sociales, de ses assurances, de ses qualifications et de la facture relative aux travaux.`,
      ]],
      ['2. Conditions d’inscription', [
        'L’utilisateur doit fournir des informations exactes, maintenir ses coordonnées à jour et protéger son compte.',
        'L’artisan doit agir dans un cadre professionnel déclaré et fournir les justificatifs demandés, notamment son identité, son SIREN/SIRET, ses assurances professionnelles et, lorsque nécessaire, ses qualifications.',
        'La plateforme peut suspendre un profil incomplet, trompeur, non assuré ou présentant un risque pour les utilisateurs.',
      ]],
      ['3. Devis et contrat de travaux', [
        'L’artisan fixe librement son prix et remet un devis conforme aux règles applicables à son activité.',
        'Le devis accepté constitue le contrat entre le client et l’artisan. Les dates, matériaux, prix HT/TTC, TVA, frais de déplacement, acompte, conditions d’annulation et garanties doivent être clairement indiqués.',
        'Toute modification substantielle doit être acceptée dans l’application et conservée dans l’historique du projet.',
      ]],
      ['4. Paiement en plusieurs étapes via Stripe', [
        `Le plan standard prévoit un acompte de ${percent(DEFAULT_DEPOSIT_RATE)} à l’acceptation du devis et le solde après confirmation de la fin des travaux. Le devis peut prévoir un autre échéancier lorsque la nature du chantier le justifie.`,
        'L’artisan est le vendeur de la prestation. Le client paie un prix total unique. Stripe déduit ses frais de traitement du compte connecté de l’artisan ; ces frais ne sont ni encaissés ni conservés par Le Petit Boulot.',
        'Le paiement par étapes améliore la traçabilité mais ne constitue ni un séquestre réglementé, ni une assurance, ni une garantie intégrale d’exécution.',
      ]],
      ['5. Frais de plateforme', [
        `Le prix total protégé inclut les services Le Petit Boulot. La part de service cible représente ${percent(PLATFORM_SHARE_RATE)} du prix client, avec un minimum et un plafond affichés avant l’envoi du devis.`,
        'Aucun frais de service supplémentaire n’est ajouté au moment du paiement.',
        'L’artisan voit avant l’envoi le prix client, les services LPB, une estimation des frais Stripe et le montant estimé qui lui restera.',
        'La plateforme facture uniquement ses propres services d’intermédiation à l’artisan. L’artisan facture les travaux au client. Le montant total du chantier n’est pas présenté comme le chiffre d’affaires de LPB ; seule la rémunération LPB constitue son produit, sous réserve du traitement comptable et fiscal validé par son conseil.',
      ]],
      ['6. Indépendance des artisans', [
        'Les artisans choisissent leurs missions, leurs prix, leurs méthodes, leurs disponibilités, leurs outils et l’organisation de leur travail.',
        'Aucune exclusivité n’est imposée. L’artisan peut travailler avec d’autres clients ou plateformes.',
        'Les règles de confiance, de sécurité, de classement et de qualité de service ne créent pas de lien de subordination.',
      ]],
      ['7. Avis et réputation', [
        'Seuls les utilisateurs liés à un projet vérifié peuvent publier un avis.',
        'Les avis peuvent être masqués jusqu’à la publication de l’avis de l’autre partie ou jusqu’à l’expiration du délai de publication.',
        'Les faux avis, contenus discriminatoires, menaces, divulgations de données privées et tentatives de manipulation sont interdits.',
      ]],
      ['8. Contournement et transactions hors plateforme', [
        'Les utilisateurs restent libres de contracter. Cependant, une transaction conclue ou payée hors plateforme ne bénéficie pas de la traçabilité, du paiement par étapes, du support, de la réputation vérifiée ni de l’historique LPB.',
        'La plateforme peut limiter la visibilité ou suspendre les comptes qui utilisent systématiquement le service pour détourner des contacts, frauder les frais annoncés ou contourner les protections.',
      ]],
      ['9. Annulation, rétractation et remboursement', [
        'Les droits de rétractation et d’annulation applicables dépendent du contrat, du moment de l’exécution et de la qualité professionnelle ou consommateur des parties.',
        'Lorsqu’un acompte est expressément qualifié comme tel, il engage fermement les parties, sous réserve des droits légaux applicables.',
        'Les remboursements sont traités selon le motif, l’état du chantier, les prestations déjà exécutées et les règles Stripe. Les frais de paiement initiaux peuvent ne pas être remboursés par le prestataire de paiement.',
      ]],
      ['10. Litiges et médiation', [
        'Les parties doivent d’abord utiliser la messagerie et le centre de résolution de la plateforme.',
        `Pour un litige relatif au service de plateforme, le consommateur peut contacter ${LEGAL.supportEmail}, puis le médiateur suivant : ${LEGAL.consumerMediator}.`,
        'Les litiges relatifs à la qualité ou à l’exécution des travaux opposent principalement le client et l’artisan, sans préjudice du support documentaire fourni par la plateforme.',
      ]],
      ['11. Données et fiscalité', [
        'La plateforme conserve les données nécessaires à la sécurité, à la preuve, au paiement, au support, à la réputation et aux obligations légales.',
        'Les artisans restent responsables de leurs déclarations fiscales et sociales. La plateforme peut transmettre aux autorités les informations imposées aux opérateurs de plateforme et fournir un relevé annuel des transactions.',
      ]],
      ['12. Responsabilité', [
        'La plateforme met en œuvre des moyens raisonnables de vérification et de sécurité mais ne garantit pas l’absence totale de fraude, d’incident ou de défaut d’exécution.',
        'Aucune clause ne limite les droits impératifs des consommateurs ni les responsabilités qui ne peuvent être exclues par la loi.',
      ]],
    ],
  },
  '/privacy': {
    title: 'Politique de confidentialité',
    intro: 'Cette version décrit le fonctionnement prévu. Complétez les coordonnées et faites valider les durées de conservation, bases légales et sous-traitants avant le lancement commercial.',
    sections: [
      ['Données collectées', [
        'Identité, coordonnées, rôle, données professionnelles, documents de vérification, projets, devis, contrats, paiements, messages, avis, photos et données techniques.',
        'La localisation précise est demandée uniquement après une action de l’utilisateur et sert à l’adresse d’un chantier, au calcul de distance ou à la navigation. Les visiteurs publics ne voient qu’une localisation approximative.',
      ]],
      ['Finalités', [
        'Créer et sécuriser les comptes, mettre les utilisateurs en relation, gérer les projets, traiter les paiements, prévenir la fraude, calculer la réputation, assurer le support et respecter les obligations légales.',
      ]],
      ['Partage', [
        'Les données sont partagées uniquement avec les parties au projet, les prestataires nécessaires tels que Firebase, Google Maps et Stripe, ainsi qu’avec les autorités lorsque la loi l’exige.',
        'Les données de géolocalisation ne sont pas revendues à des fins publicitaires.',
      ]],
      ['Vos droits', [
        `Vous pouvez demander l’accès, la rectification, l’effacement, la limitation ou la portabilité de vos données en écrivant à ${LEGAL.privacyEmail}.`,
        'Certaines données doivent être conservées pour la comptabilité, les obligations fiscales, la preuve contractuelle, la prévention de la fraude ou la gestion des litiges.',
      ]],
    ],
  },
  '/review-policy': {
    title: 'Fonctionnement des avis vérifiés',
    intro: 'Les avis LPB sont liés à une interaction documentée dans la plateforme.',
    sections: [
      ['Vérification', [
        'Un avis est qualifié de vérifié uniquement lorsque son auteur a participé au projet concerné et que le projet a atteint l’étape de paiement ou de clôture.',
        'La date du projet et la date de publication sont affichées.',
      ]],
      ['Publication', [
        'Les avis des deux parties sont masqués pendant la période de réponse afin de limiter les avis de représailles.',
        'Ils sont publiés lorsque les deux avis sont reçus ou automatiquement après la fin du délai prévu.',
      ]],
      ['Modération', [
        'Un avis peut être refusé ou masqué s’il contient des données personnelles, des insultes, des menaces, des accusations pénales non étayées, du contenu discriminatoire, de la publicité ou un conflit d’intérêts.',
        'Une mauvaise note ou une critique argumentée ne constitue pas, à elle seule, un motif de suppression.',
      ]],
      ['Classement', [
        'Par défaut, les avis sont classés du plus récent au plus ancien. Les notes affichées tiennent compte du nombre d’avis et du niveau de confiance statistique.',
      ]],
    ],
  },
  '/fees': {
    title: 'Tarifs et flux financiers',
    intro: 'Une tarification simple, affichée avant paiement et conçue pour maintenir les transactions dans la plateforme.',
    sections: [
      ['Tarification recommandée au lancement', [
        `Prix protégé : les services Le Petit Boulot représentent ${percent(PLATFORM_SHARE_RATE)} du prix client, avec le minimum et le plafond affichés avant l’envoi.`,
        `Aucun frais client supplémentaire n’est ajouté au moment du paiement.`,
        `Acompte standard : ${percent(DEFAULT_DEPOSIT_RATE)} à l’acceptation du devis, puis solde après confirmation des travaux.`,
      ]],
      ['Qui facture quoi ?', [
        'L’artisan facture au client la prestation de travaux, ses matériaux, sa main-d’œuvre et la TVA applicable à son activité.',
        'LPB facture à l’artisan ses services d’intermédiation, de gestion et de paiement selon le prix protégé présenté avant l’acceptation. Le prix client reste unique et aucun supplément n’est ajouté au passage en caisse.',
        'Stripe traite le paiement directement sur le compte connecté de l’artisan. Stripe facture ses propres frais à ce compte ; ils sont distincts des services LPB et ne constituent pas un revenu LPB.',
      ]],
      ['Évolution', [
        'Les frais peuvent évoluer avec un préavis raisonnable. Le taux applicable à un projet est celui présenté et accepté avant le paiement.',
        'Des niveaux de confiance peuvent réduire la commission artisan sans acheter un meilleur classement organique.',
      ]],
    ],
  },
  '/ranking': {
    title: 'Référencement et classement',
    intro: 'Le classement organique repose sur la pertinence et la confiance, pas uniquement sur la rémunération de la plateforme.',
    sections: [
      ['Critères principaux', [
        'Compatibilité métier, distance approximative, disponibilité, expérience vérifiée, note ajustée, taux de réponse, taux d’achèvement, ponctualité, annulations et qualité du profil.',
      ]],
      ['Nouveaux profils', [
        'Les nouveaux artisans sont identifiés comme nouveaux et bénéficient d’une exposition minimale afin de pouvoir construire leur historique.',
      ]],
      ['Contenus sponsorisés', [
        'Tout placement payé sera clairement identifié comme sponsorisé et séparé du classement organique.',
      ]],
    ],
  },
};

const LegalPage = () => {
  const { pathname } = useLocation();
  const page = legalPages[pathname] || legalPages['/terms'];

  return (
    <Page>
      <Header>
        <Link to="/landing">← Retour</Link>
        <h1>{page.title}</h1>
        <p>{page.intro}</p>
      </Header>
      <Body>
        {page.sections.map(([title, paragraphs]) => (
          <Section key={title}>
            <h2>{title}</h2>
            {paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </Section>
        ))}
      </Body>
      <Notice>
        <strong>À compléter avant lancement :</strong> raison sociale, forme juridique, siège, SIREN,
        numéro de TVA, directeur de publication, médiateur de la consommation et coordonnées de l’hébergeur.
      </Notice>
    </Page>
  );
};

const Page = styled.main`width:min(100% - 2rem,68rem);margin:0 auto;padding:clamp(1.5rem,4vw,3rem) 0 5rem;`;
const Header = styled.header`padding:clamp(1.3rem,4vw,2.4rem);border:1px solid var(--color-border);border-radius:1.4rem;background:#fff;box-shadow:var(--shadow-sm);a{display:inline-flex;margin-bottom:1rem;color:#755a31;font-weight:800;text-decoration:none;}h1{margin:0;font-size:clamp(1.8rem,5vw,3rem);}p{max-width:52rem;margin:.75rem 0 0;color:var(--color-textMuted);line-height:1.65;}`;
const Body = styled.div`display:grid;gap:1rem;margin-top:1rem;`;
const Section = styled.section`padding:clamp(1.15rem,3vw,1.7rem);border:1px solid var(--color-border);border-radius:1.1rem;background:#fff;h2{margin:0 0 .75rem;font-size:1.12rem;}p{margin:.5rem 0;color:#4f4b45;line-height:1.7;}`;
const Notice = styled.aside`margin-top:1rem;padding:1rem 1.15rem;border:1px solid #e1c98f;border-radius:1rem;background:#fff8e7;color:#684f1f;line-height:1.55;`;

export default LegalPage;
